import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { RotateLeft, Save, History } from "@material-ui/icons";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import MDEditor from "@uiw/react-md-editor";
import Button from "components/CustomButtons/Button.js";
import NotificationContext from "views/Components/Context.js";
import { fetchPDFAPI } from "views/APIs/APIUtility";
import {
  listArtifacts,
  recompileTexToPdf,
  updateTexArtifact,
  fetchTexFromS3,
} from "views/APIs/NewProjectArtifacts";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const pdfOptions = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    marginBottom: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  splitContainer: {
    position: "relative",
    marginTop: theme.spacing(2),
  },
  editorPane: {
    height: "600px",
    overflow: "hidden",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
  },
  pdfPane: {
    height: "600px",
    overflow: "auto",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    backgroundColor: "#f5f5f5",
  },
  centerButton: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1000,
  },
  recompileButton: {
    borderRadius: "50%",
    padding: "12px",
    minWidth: "auto",
    width: "48px",
    height: "48px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    flexDirection: "column",
  },
  versionSelect: {
    minWidth: 150,
    marginRight: theme.spacing(2),
  },
  actionBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  pdfPage: {
    margin: "10px auto",
  },
  noArtifact: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "400px",
    flexDirection: "column",
    color: theme.palette.text.secondary,
  },
  wrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: theme.palette.success.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

export default function TexPdfViewer({
  projectId,
  stage,
  artifactType, // e.g., "slide_deck", "lecture_notes", "slide_deck_with_notes"
  disabled = false,
  onContentChange,
  onArtifactSelect, // Callback when artifact/version is selected
}) {
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);
  const containerRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recompiling, setRecompiling] = useState(false);

  // Artifact state
  const [texArtifact, setTexArtifact] = useState(null);
  const [pdfArtifact, setPdfArtifact] = useState(null);

  // Content state
  const [texContent, setTexContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // PDF state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(500);

  // Version state
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState("latest");

  // prevent re-initializing dropdown after user manually changes it
  const didInitDefaultVersion = useRef(false);

  // Fetch artifacts for this stage
  const fetchArtifacts = async () => {
    if (!projectId) return;

    setLoading(true);
    // Reset all state when fetching new artifacts to prevent old content from showing
    setPdfFile(null);
    setPdfArtifact(null);
    setTexArtifact(null);
    setTexContent("");
    setOriginalContent("");
    setHasUnsavedChanges(false);
    setPdfLoading(false);
    setNumPages(null);
    didInitDefaultVersion.current = false;
    
    try {
      const allArtifacts = await listArtifacts(projectId);

      // Find TeX and PDF artifacts for this stage type
      const texTypeKey = `${artifactType}_tex`;
      const pdfTypeKey = `${artifactType}_pdf`;

      // Filter all candidates for this artifact type across both tex and pdf
      const candidates = allArtifacts.filter(
        (a) =>
          a.type === texTypeKey ||
          a.type === `${artifactType}.tex` ||
          a.type === pdfTypeKey ||
          a.type === `${artifactType}.pdf`,
      );

      // Group by run_id. Each run usually contains both a TeX and a PDF artifact.
      const groupedByRun = candidates.reduce((acc, a) => {
        if (!acc[a.run_id]) {
          acc[a.run_id] = { run_id: a.run_id, tex: null, pdf: null };
        }
        
        // Determine if artifact is tex or pdf
        const isTex = a.format === "tex" || a.type.includes("_tex") || a.type.endsWith(".tex");
        const isPdf = a.format === "pdf" || a.type.includes("_pdf") || a.type.endsWith(".pdf");

        if (isTex) acc[a.run_id].tex = a;
        if (isPdf) acc[a.run_id].pdf = a;
        
        return acc;
      }, {});

      // Sort runs by ID (assuming lexicographical order corresponds to time)
      const sortedVersions = Object.values(groupedByRun).sort((a, b) =>
        b.run_id.localeCompare(a.run_id),
      );

      setVersions(sortedVersions);

      // Default to the newest complete version (tex+pdf).
      // Some historical cloned projects may have tex/pdf split across runs.
      const latest = sortedVersions[0] || null;
      const initialVersion =
        sortedVersions.find((v) => v.tex && v.pdf) ||
        sortedVersions.find((v) => v.tex) ||
        latest;

      setTexArtifact(initialVersion?.tex || null);
      setPdfArtifact(initialVersion?.pdf || null);

      if (initialVersion?.tex) {
        if (!didInitDefaultVersion.current) {
          setSelectedVersion(
            initialVersion.run_id === latest?.run_id ? "latest" : initialVersion.run_id
          );
          didInitDefaultVersion.current = true;
        }
        await loadTexContent(initialVersion.tex);
      } else {
        // Clear TeX content if no artifact found
        setTexContent("");
        setOriginalContent("");
      }

      if (initialVersion?.pdf) {
        await loadPdf(initialVersion.pdf.s3?.public_url);

        // Notify parent of selected artifact (use PDF artifact ID)
        if (onArtifactSelect && initialVersion.pdf.artifact_id) {
          onArtifactSelect(initialVersion.pdf.artifact_id);
        }
      } else {
        // Clear PDF if no artifact found
        setPdfFile(null);
        setPdfLoading(false);
      }
    } catch (error) {
      console.error("Error fetching artifacts:", error);
      showNotification("Error loading artifacts", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Load TeX content from S3
  const loadTexContent = async (artifact) => {
    if (!artifact?.s3?.public_url) return;

    try {
      const data = await fetchTexFromS3(projectId, artifact.s3.public_url);
      // backend may return { tex: '...' } or { markdown: '...' }
      const content = data?.tex ?? data?.markdown ?? "";
      setTexContent(content);
      setOriginalContent(content);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error loading TeX content:", error);
      showNotification("Error loading TeX content", "danger");
    }
  };

  // Load PDF
  const loadPdf = async (url) => {
    if (!url) return;

    setPdfLoading(true);
    try {
      const formData = new FormData();
      formData.append("url", url);
      const response = await fetchPDFAPI(formData);
      const pdfBlob = await response.blob();
      const pdfBlobUrl = URL.createObjectURL(pdfBlob);
      setPdfFile(pdfBlobUrl);
    } catch (error) {
      console.error("Error loading PDF:", error);
      // Try opening directly if fetch fails
      setPdfFile(url);
    } finally {
      setPdfLoading(false);
    }
  };

  // Handle TeX content change
  const handleTexChange = (newContent) => {
    setTexContent(newContent);
    setHasUnsavedChanges(newContent !== originalContent);

    if (onContentChange) {
      onContentChange(newContent);
    }
  };

  // Save TeX changes (creates new version)
  const handleSave = async () => {
    if (!texArtifact || !texContent) return;

    setSaving(true);
    try {
      await updateTexArtifact(projectId, texArtifact.artifact_id, texContent);
      setOriginalContent(texContent);
      setHasUnsavedChanges(false);
      showNotification("TeX content saved successfully", "success");

      // Refresh data
      await fetchArtifacts();
    } catch (error) {
      console.error("Error saving TeX:", error);
      showNotification("Error saving TeX content", "danger");
    } finally {
      setSaving(false);
    }
  };

  // Recompile TeX to PDF
  const handleRecompile = async () => {
    if (!texArtifact) return;

    // If there are unsaved changes, save first
    if (hasUnsavedChanges) {
      await handleSave();
    }

    setRecompiling(true);
    setPdfLoading(true);
    try {
      const result = await recompileTexToPdf(projectId, {
        texContent: texContent,
        sourceArtifactIds: texArtifact.artifact_id
          ? [texArtifact.artifact_id]
          : [],
        basename: artifactType || "document",
      });

      // Load the new PDF
      if (result.pdf_artifact?.s3?.public_url) {
        await loadPdf(result.pdf_artifact.s3.public_url);
        setPdfArtifact(result.pdf_artifact);
      }

      showNotification("PDF recompiled successfully", "success");
      
      // Refresh to show the new run/version in dropdown
      await fetchArtifacts();
    } catch (error) {
      console.error("Error recompiling:", error);
      showNotification("Error recompiling PDF", "danger");
    } finally {
      setRecompiling(false);
      setPdfLoading(false);
    }
  };

  // Handle version selection
  const handleVersionChange = async (event) => {
    const runId = event.target.value;
    setSelectedVersion(runId);

    if (runId === "latest") {
      const latest = versions[0];
      if (latest?.tex) await loadTexContent(latest.tex);
      if (latest?.pdf) {
        await loadPdf(latest.pdf.s3?.public_url);
        if (onArtifactSelect) onArtifactSelect(latest.pdf.artifact_id);
      }
    } else {
      // Load specific run context (TeX + PDF)
      const version = versions.find((v) => v.run_id === runId);
      
      if (version?.tex) {
        await loadTexContent(version.tex);
      }
      
      if (version?.pdf) {
        await loadPdf(version.pdf.s3?.public_url);
        // Notify parent of selected artifact (use PDF artifact ID)
        if (onArtifactSelect) {
          onArtifactSelect(version.pdf.artifact_id);
        }
      }
    }
  };

  // Download TeX file
  const handleDownloadTex = async () => {
    if (!texArtifact?.s3?.public_url) return;

    try {
      const data = await fetchTexFromS3(projectId, texArtifact.s3.public_url);
      const content = data?.tex ?? data?.markdown ?? "";
      const blob = new Blob([content], { type: "application/x-tex" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${artifactType || "document"}.tex`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification("TeX file downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading TeX file:", error);
      showNotification("Error downloading TeX file", "danger");
    }
  };

  // Download PDF file
  const handleDownloadPdf = async () => {
    if (!pdfArtifact?.s3?.public_url) return;

    try {
      const formData = new FormData();
      formData.append("url", pdfArtifact.s3.public_url);
      const response = await fetchPDFAPI(formData);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${artifactType || "document"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showNotification("PDF file downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading PDF file:", error);
      showNotification("Error downloading PDF file", "danger");
    }
  };

  // PDF document load success
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Update container width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    // Wait for container to be fully rendered before measuring
    setTimeout(() => {
      updateWidth();
    }, 2000);

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [pdfFile]);

  // Initial fetch
  useEffect(() => {
    fetchArtifacts();
  }, [projectId, artifactType]);

  // Check if we have artifacts to display
  const hasArtifacts = texArtifact || pdfArtifact;
  const isLatestVersion = selectedVersion === "latest";

  if (loading) {
    return (
      <Box className={classes.loadingContainer} style={{ minHeight: 400 }}>
        <CircularProgress />
        <Typography variant="body2" style={{ marginTop: 16 }}>
          Loading artifacts...
        </Typography>
      </Box>
    );
  }

  if (!hasArtifacts) {
    return (
      <Box className={classes.noArtifact}>
        <Typography variant="h5">No Artifacts Available</Typography>
        <Typography variant="body2">
          Run the pipeline from the previous stage to generate artifacts.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          {artifactType
            ?.replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}
        </Typography>
      </Box>

      {/* Action bar */}
      <Box className={classes.actionBar}>
        {versions.length > 0 && (
          <FormControl
            variant="outlined"
            size="small"
            className={classes.versionSelect}
          >
            <InputLabel>Version</InputLabel>
            <Select
              value={selectedVersion}
              onChange={handleVersionChange}
              label="Version"
            >
              <MenuItem value="latest">Latest</MenuItem>
              {versions.map((v, idx) => (
                <MenuItem key={v.run_id} value={v.run_id}>
                  Version {versions.length - idx}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {(hasUnsavedChanges || saving) && isLatestVersion && (
          <div className={classes.wrapper}>
            <Button
              color="success"
              size="sm"
              onClick={handleSave}
              disabled={saving || disabled}
              startIcon={!saving ? <Save /> : null}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            {saving && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        )}

        {!isLatestVersion && (
          <Typography variant="caption" color="textSecondary">
            Viewing historical version (read-only)
          </Typography>
        )}
      </Box>

      <Divider style={{ marginBottom: 16 }} />

      {/* Split view: TeX Editor | PDF Viewer */}
      <Grid container spacing={2} className={classes.splitContainer}>
        {/* TeX Editor (Left Side) */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            TeX Editor
          </Typography>
          <div className={classes.editorPane} data-color-mode="light">
            <MDEditor
              value={texContent}
              onChange={handleTexChange}
              preview="edit"
              height={580}
              hideToolbar={false}
              visibleDragbar={false}
              textareaProps={{
                placeholder: "TeX content will appear here...",
                disabled: disabled || !isLatestVersion,
              }}
            />
          </div>
        </Grid>

        {/* Recompile Button (Center) */}
        <div className={classes.centerButton}>
          <Tooltip
            title={hasUnsavedChanges ? "Save & Recompile" : "Recompile PDF"}
          >
            <span>
              <Button
                onClick={handleRecompile}
                color="quCreateColor"
                className={classes.recompileButton}
                disabled={
                  disabled || !texArtifact || recompiling || !isLatestVersion
                }
              >
                {recompiling ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <RotateLeft />
                )}
              </Button>
            </span>
          </Tooltip>
        </div>

        {/* PDF Viewer (Right Side) */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" gutterBottom>
            PDF Preview
          </Typography>
          <div
            ref={containerRef}
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              height: "600px",
              width: "100%",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              backgroundColor: "#f5f5f5",
            }}
          >
            {pdfLoading ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  {recompiling ? "Recompiling..." : "Loading PDF..."}
                </Typography>
              </Box>
            ) : pdfFile ? (
              <Document
                file={pdfFile}
                onLoadSuccess={onDocumentLoadSuccess}
                options={pdfOptions}
              >
                {Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={containerWidth}
                    style={{ margin: "10px 0" }}
                  />
                ))}
              </Document>
            ) : (
              <Box className={classes.loadingContainer}>
                <Typography variant="body2" color="textSecondary">
                  No PDF available. Click recompile to generate.
                </Typography>
              </Box>
            )}
          </div>
        </Grid>
      </Grid>

      {/* Download buttons */}
      <Box style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: "8px" }}>
        <Button
          color="info"
          size="sm"
          onClick={handleDownloadTex}
          disabled={!texArtifact}
        >
          Download .tex File
        </Button>
        <Button
          color="info"
          size="sm"
          onClick={handleDownloadPdf}
          disabled={!pdfArtifact}
        >
          Download .pdf File
        </Button>
      </Box>
    </Box>
  );
}
